/** Cropped worker figure: only the boots and trousers visible above Rosalie's sightline. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

const LEFT_X = -0.12;
const RIGHT_X = 0.12;
// Shifted forward by 32 mm from the brief's figures so the PAIR is centred in Z.
// The trailing trouser reaches further back than the leading toe reaches forward
// — a leg is not symmetric about its ankle — so centring the two ankle points
// leaves the whole assembly off-centre, which the asset contract catches to
// within 30 mm. The 0.14 m stride between them is unchanged; it is the staging.
const LEFT_ANKLE_Z = 0.012;
const RIGHT_ANKLE_Z = -0.128;

function addBoot(
  group: THREE.Group,
  side: 'left' | 'right',
  x: number,
  ankleZ: number,
  bootMaterial: THREE.Material,
  soleMaterial: THREE.Material,
): void {
  const sole = new THREE.Mesh(new THREE.BoxGeometry(0.125, 0.012, 0.28), soleMaterial);
  sole.name = `${side}-boot-sole`;
  sole.position.set(x, 0.006, ankleZ + 0.09);
  sole.castShadow = true;
  group.add(sole);

  const foot = new THREE.Mesh(new THREE.BoxGeometry(0.115, 0.055, 0.21), bootMaterial);
  foot.name = `${side}-boot-foot`;
  foot.position.set(x, 0.0395, ankleZ + 0.055);
  foot.castShadow = true;
  group.add(foot);

  const toe = new THREE.Mesh(new THREE.SphereGeometry(1, 8, 4), bootMaterial);
  toe.name = `${side}-boot-rounded-toe`;
  toe.scale.set(0.0575, 0.0275, 0.07);
  toe.position.set(x, 0.0395, ankleZ + 0.16);
  toe.castShadow = true;
  group.add(toe);

  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.075, 0.345, 12), bootMaterial);
  shaft.name = `${side}-boot-shaft`;
  shaft.position.set(x, 0.2275, ankleZ);
  shaft.castShadow = true;
  group.add(shaft);
}

function addTrouserLeg(
  group: THREE.Group,
  side: 'left' | 'right',
  x: number,
  ankleZ: number,
  trouserMaterial: THREE.Material,
): void {
  const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.115, 0.095, 0.64, 12), trouserMaterial);
  leg.name = `${side}-trouser-leg`;
  leg.position.set(x, 0.68, ankleZ);
  leg.castShadow = true;
  group.add(leg);

  // Without this small break in the silhouette, the specified straight, cropped
  // leg reads as a concrete bollard rather than trousers above a rubber boot.
  const kneeFold = new THREE.Mesh(new THREE.CylinderGeometry(0.102, 0.102, 0.08, 12), trouserMaterial);
  kneeFold.name = `${side}-trouser-knee-fold`;
  kneeFold.position.set(x, 0.55, ankleZ);
  kneeFold.castShadow = true;
  group.add(kneeFold);
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'worker-legs';
  // Rubber boots and workwear use organic because Rosalie's monotonic D-002 wear
  // channel must never make a worker visibly age as the story progresses.
  const bootMaterial = surface('organic', 0x242c2a);
  const soleMaterial = surface('organic', 0x15191a);
  const trouserMaterial = surface('organic', 0x3c4550);

  addBoot(group, 'left', LEFT_X, LEFT_ANKLE_Z, bootMaterial, soleMaterial);
  addBoot(group, 'right', RIGHT_X, RIGHT_ANKLE_Z, bootMaterial, soleMaterial);
  addTrouserLeg(group, 'left', LEFT_X, LEFT_ANKLE_Z, trouserMaterial);
  addTrouserLeg(group, 'right', RIGHT_X, RIGHT_ANKLE_Z, trouserMaterial);
  return group;
}

export const workerLegsAsset: AssetDefinition = {
  id: 'worker-legs',
  label: 'Worker legs',
  description: 'Rubber farm boots and cropped trouser legs, staged mid-stride.',
  // Length measured off the geometry rather than taken from the brief: the
  // trailing trouser's radius adds to the stride, so the pair is 0.49 deep.
  footprint: { width: 0.48, height: 1.0, length: 0.49 },
  create: build,
};
